import React, { useEffect, useRef, useState } from 'react'
import api from 'api/api'
import useStore from 'state/knovStore'
import toast from 'utils/toast'
import { confirmAsync } from 'components/shared/confirmUtils'
import { showOrdinalMintProgressToast } from 'components/shared/OrdinalMintStatusListener'
import {
    cancelSystemThreadOpen,
    openSystemThreadWhenMinted,
} from 'components/shared/ordinalMintNavigation'
import { SATOSHIS } from 'lib/bsv-util'
import {
    LOCAL_ORDINAL_MAX_PREVOUT_SCRIPT_BYTES,
    ordinalMintLockingScriptBytes,
} from 'lib/ordinal-local-mint-signing'
import {
    buildMintMediaThreadConfirmation,
    createMintMediaThreadRunner,
    createMintSourceRetryCache,
    mintableMediaKindForFile,
    normalizeMintMediaFile,
    mintSourceRetryKey,
    type MintableMediaKind,
    type MintMediaThreadFlowOptions,
} from 'components/quests/mintMediaThreadPolicy'

// Same pricing model as the per-media mint buttons (Video.jsx / AnswerImage).
const MINT_TX_OVERHEAD_BYTES = 600
const MINT_FEE_SAFETY_MARGIN = 1.2
const MINTABLE_VIDEO_MAX_MB = 50
const MINTABLE_VIDEO_MAX_BYTES = MINTABLE_VIDEO_MAX_MB * 1024 * 1024
// Matches the server's MINT_HTML_MAX_MB.
const MINTABLE_HTML_MAX_MB = 10
const MINTABLE_HTML_MAX_BYTES = MINTABLE_HTML_MAX_MB * 1024 * 1024
const MEDIA_ACCEPT = 'image/*,video/*,audio/*,.html,.htm,text/html'

// One toast slot for the whole pre-workflow stretch (upload + post + mint kickoff);
// The answer-scoped mint toast takes over the slot once an answer id exists.
const FLOW_TOAST_KIND = 'mint-media-new-thread'
const failedMintSources = createMintSourceRetryCache()

export function pickMediaFile(accept: string): Promise<File | null> {
    return new Promise(resolve => {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = accept
        input.style.display = 'none'
        document.body.appendChild(input)

        let settled = false
        let dismissalTimer: number | null = null

        const finish = (file: File | null) => {
            if (settled) return
            settled = true
            if (dismissalTimer !== null) window.clearTimeout(dismissalTimer)
            window.removeEventListener('focus', scheduleDismissalCheck)
            document.removeEventListener('visibilitychange', scheduleDismissalCheck)
            input.remove()
            resolve(file)
        }

        const scheduleDismissalCheck = () => {
            if (document.visibilityState === 'hidden') return
            if (dismissalTimer !== null) window.clearTimeout(dismissalTimer)
            // Some browsers do not emit the input `cancel` event. Once focus or
            // visibility returns, give `change` a chance to fire before treating
            // an empty input as a dismissed picker.
            dismissalTimer = window.setTimeout(() => finish(input.files?.[0] || null), 300)
        }

        input.addEventListener('change', () => finish(input.files?.[0] || null))
        input.addEventListener('cancel', () => finish(null))
        window.addEventListener('focus', scheduleDismissalCheck)
        document.addEventListener('visibilitychange', scheduleDismissalCheck)

        try {
            input.click()
        } catch {
            finish(null)
        }
    })
}

function formatBytes(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
    return `${Math.max(1, Math.round(bytes / 1024))}KB`
}

// Live preview of a picked HTML page. `allow-scripts` without `allow-same-origin`
// gives the frame an opaque origin: the page runs, but cannot reach Treechat's
// cookies, storage, or DOM.
//
// The page only gets its markup once the frame has a laid-out size. An inline
// srcdoc can run before the frame's first layout; a page that sizes itself from
// innerWidth then builds a 1x1 canvas, and the resize to the real size lands
// before it listens for one, so it stays blank.
function HtmlMintPreview({ html, title }: { html: string; title: string }) {
    const frameRef = useRef<HTMLIFrameElement>(null)
    const [sized, setSized] = useState(false)

    useEffect(() => {
        const frame = frameRef.current
        if (!frame) return
        let raf = 0
        const settle = () => {
            if (frame.clientWidth > 0 && frame.clientHeight > 0) {
                // One more frame so the size is committed before the page runs.
                raf = requestAnimationFrame(() => setSized(true))
                return true
            }
            return false
        }
        if (settle()) return () => cancelAnimationFrame(raf)
        const observer = new ResizeObserver(() => {
            if (settle()) observer.disconnect()
        })
        observer.observe(frame)
        return () => {
            observer.disconnect()
            cancelAnimationFrame(raf)
        }
    }, [])

    return (
        <iframe
            ref={frameRef}
            title={title}
            srcDoc={sized ? html : undefined}
            sandbox="allow-scripts"
            referrerPolicy="no-referrer"
            data-testid="mint-html-preview"
            style={{
                display: 'block',
                width: '100%',
                height: '240px',
                border: '1px solid var(--color-border-primary)',
                borderRadius: '10px',
                background: '#000',
                marginBottom: '6px',
            }}
        />
    )
}

function confirmMintTray({
    byteSize,
    filename,
    mediaKind,
    previewUrl,
    htmlSource,
    estimatedOnchainWriteUsd,
    treechatFeeUsd,
    estimatedOnchainWriteSats,
    treechatFeeSats,
}: {
    byteSize: number
    filename: string
    htmlSource?: string | null
    mediaKind: MintableMediaKind
    previewUrl: string
    estimatedOnchainWriteUsd: number
    treechatFeeUsd: number
    estimatedOnchainWriteSats: number
    treechatFeeSats: number
}): Promise<boolean> {
    const isVideo = mediaKind === 'video'
    const confirmation = buildMintMediaThreadConfirmation({
        byteSize,
        estimatedOnchainWriteUsd,
        treechatFeeUsd,
        estimatedOnchainWriteSats,
        treechatFeeSats,
    })
    // Keep visual previews at their natural width. Audio controls need an explicit
    // width because `auto` collapses the native player to zero width.
    const previewStyle: React.CSSProperties = {
        display: 'block',
        width: mediaKind === 'audio' ? '300px' : 'auto',
        maxWidth: '100%',
        maxHeight: '180px',
        objectFit: 'contain',
        borderRadius: '10px',
        margin: '0 auto 8px',
    }

    return new Promise(resolve => {
        const { openConfirmTray } = useStore.getState().actions.trayActions

        openConfirmTray(
            <div style={{ color: 'var(--color-text-primary)' }}>
                <div
                    style={{
                        fontWeight: 600,
                        fontSize: '12px',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        opacity: 0.7,
                        marginBottom: '8px',
                    }}
                >
                    Mint Ordinal
                </div>

                {mediaKind === 'html' ? (
                    <div style={{ margin: '0 0 8px' }}>
                        {htmlSource != null && (
                            <HtmlMintPreview html={htmlSource} title={`Preview of ${filename}`} />
                        )}
                        <div style={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
                            {filename} · {formatBytes(byteSize)}
                        </div>
                    </div>
                ) : isVideo ? (
                    <video
                        src={previewUrl}
                        muted
                        playsInline
                        preload="metadata"
                        style={previewStyle}
                    />
                ) : mediaKind === 'audio' ? (
                    <audio src={previewUrl} controls preload="metadata" style={previewStyle} />
                ) : (
                    <img src={previewUrl} alt="Selected media" style={previewStyle} />
                )}

                <div style={{ opacity: 0.9 }}>{confirmation.description}</div>
            </div>,
            confirmation.confirmLabel,
            () => resolve(true),
            {
                onCancel: () => resolve(false),
                hideCancel: true,
                confirmVariant: 'bsv',
                primaryActionFirst: true,
            },
        )
    })
}

/**
 * The ordinals stream's "New Thread" experience: pick a media file, pay the mint
 * gate, then inscribe it from a hidden post; the ordinal's system thread opens once
 * it lands (ordinalMintNavigation). Once the
 * workflow starts, OrdinalMintStatusListener owns progress/completion UX.
 */
async function performMintMediaThreadFlow({ source }: MintMediaThreadFlowOptions = {}) {
    if (!window.gon?.currentUser) {
        useStore.getState().actions.modalActions.openAuthModal('signup', 'ordinals_mint_media')
        return
    }

    const pickedFile = await pickMediaFile(MEDIA_ACCEPT)
    if (!pickedFile) return

    const mediaKind = mintableMediaKindForFile(pickedFile)
    // An .html file picked with an empty MIME type would upload as octet-stream and
    // inscribe as a download instead of a page.
    const file = normalizeMintMediaFile(pickedFile)
    if (!mediaKind) {
        await confirmAsync('Choose an image, video, audio, or HTML file to mint.', {
            confirmLabel: 'OK',
            hideCancel: true,
            iconType: 'error',
        })
        return
    }

    if (file.size <= 0) {
        await confirmAsync('The selected media file is empty. Choose another file to mint.', {
            confirmLabel: 'OK',
            hideCancel: true,
            iconType: 'error',
        })
        return
    }

    if (mediaKind === 'html' && file.size > MINTABLE_HTML_MAX_BYTES) {
        await confirmAsync(
            `HTML file is too large to mint (${formatBytes(
                file.size,
            )}). Max supported size is ${MINTABLE_HTML_MAX_MB}MB.`,
            { confirmLabel: 'OK', hideCancel: true, iconType: 'error' },
        )
        return
    }

    const isVideo = mediaKind === 'video'
    if (isVideo && file.size > MINTABLE_VIDEO_MAX_BYTES) {
        await confirmAsync(
            `Video is too large to mint (${formatBytes(
                file.size,
            )}). Max supported size is ${MINTABLE_VIDEO_MAX_MB}MB.`,
            { confirmLabel: 'OK', hideCancel: true, iconType: 'error' },
        )
        return
    }

    const [exchangeResult, feeResult, mintFeeQuote] = await Promise.all([
        api.getBsvExchangeRate().catch(() => null),
        api.getBsvFeeRate().catch(() => null),
        api.getOrdinalMintFeeQuote().catch(() => null),
    ])
    const bsvExchangeRate = Number(exchangeResult?.exchangerate || 0)
    const fetchedFeeRate = Number(feeResult?.fee_rate)
    const bsvFeeRateSatsPerByte =
        Number.isFinite(fetchedFeeRate) && fetchedFeeRate > 0 ? fetchedFeeRate : 0.1

    if (!Number.isFinite(bsvExchangeRate) || bsvExchangeRate <= 0) {
        await confirmAsync('Unable to fetch BSV exchange rate. Please try again in a moment.', {
            confirmLabel: 'OK',
            hideCancel: true,
            iconType: 'error',
        })
        return
    }

    const mintPriceSats = Number(mintFeeQuote?.service_fee_satoshis)
    if (
        !Number.isSafeInteger(mintPriceSats) ||
        mintPriceSats <= 0 ||
        !mintFeeQuote?.service_fee_quote
    ) {
        await confirmAsync('Unable to quote the Treechat mint fee. Please try again in a moment.', {
            confirmLabel: 'OK',
            hideCancel: true,
            iconType: 'error',
        })
        return
    }
    const treechatFeeUsd = (mintPriceSats / SATOSHIS) * bsvExchangeRate
    if (
        mintFeeQuote.local_signing_device_available &&
        ordinalMintLockingScriptBytes({
            contentBytes: file.size,
            contentType: file.type.toLowerCase(),
            filename: file.name || 'media',
            app: mintFeeQuote.app,
        }) > LOCAL_ORDINAL_MAX_PREVOUT_SCRIPT_BYTES
    ) {
        await confirmAsync('This file is too large for local ordinal signing on this device.', {
            confirmLabel: 'OK',
            hideCancel: true,
            iconType: 'error',
        })
        return
    }
    const estimatedOnchainWriteSats = Math.ceil(
        (file.size + MINT_TX_OVERHEAD_BYTES) * bsvFeeRateSatsPerByte * MINT_FEE_SAFETY_MARGIN,
    )
    const estimatedOnchainWriteUsd = (estimatedOnchainWriteSats / SATOSHIS) * bsvExchangeRate

    window.logEv?.('BSV_MINT_START', {
        media_type: mediaKind,
        source: source || 'ordinals_stream_new_thread',
        mint_price_sats: mintPriceSats,
        mint_price_usd: treechatFeeUsd,
        file_bytes: file.size,
        fee_rate_sats_per_byte: bsvFeeRateSatsPerByte,
        estimated_onchain_write_sats: estimatedOnchainWriteSats,
    })

    const previewUrl = URL.createObjectURL(file)
    let htmlSource: string | null = null
    if (mediaKind === 'html') {
        try {
            htmlSource = await file.text()
        } catch (error) {
            console.error('Could not read the HTML file for preview:', error)
        }
    }
    let confirmed = false
    try {
        confirmed = await confirmMintTray({
            byteSize: file.size,
            filename: file.name || 'media',
            mediaKind,
            previewUrl,
            htmlSource,
            estimatedOnchainWriteUsd,
            treechatFeeUsd,
            estimatedOnchainWriteSats,
            treechatFeeSats: mintPriceSats,
        })
    } finally {
        URL.revokeObjectURL(previewUrl)
    }
    if (!confirmed) return

    toast.pending('Minting ordinal', { kind: FLOW_TOAST_KIND, detail: 'Uploading media…' })

    const userId = String(window.gon.currentUser.id)
    let retryKey: string
    try {
        retryKey = await mintSourceRetryKey(userId, file)
    } catch (error) {
        toast.update(FLOW_TOAST_KIND, 'Could not read the selected file — funds were not spent', {
            variant: 'error',
            detail: '',
        })
        return
    }
    // The upload goes on a hidden, owner-only post: the ordinal's system thread is
    // the only thread this mint gets, and it opens once the mint lands. A retry of
    // a failed start reuses that post and its upload.
    let answerId = failedMintSources.find(retryKey)
    if (!answerId) {
        let signedId: string | undefined
        try {
            const media = await api.preuploadAnswerMedia(
                { files: [file] },
                pct =>
                    toast.update(FLOW_TOAST_KIND, 'Minting ordinal', {
                        detail: `Uploading ${Math.min(99, Math.round(pct))}%`,
                    }),
                undefined,
                { uploadKind: 'file', source: 'mintMediaThreadFlow' },
            )
            signedId = media.files?.[0]
        } catch (error) {
            console.error('Mint media upload failed:', error)
        }
        if (!signedId) {
            // Clear the detail too, or the resolved toast keeps the stale "Uploading 99%".
            toast.update(FLOW_TOAST_KIND, 'Upload failed — funds were not spent', {
                variant: 'error',
                detail: '',
            })
            return
        }

        toast.update(FLOW_TOAST_KIND, 'Minting ordinal', { detail: 'Preparing mint…' })

        try {
            const source = await api.createMintSource(signedId)
            answerId = source.answer_id ? String(source.answer_id) : null
        } catch (error) {
            console.error('Mint source creation failed:', error)
        }
        if (!answerId) {
            toast.update(FLOW_TOAST_KIND, 'Could not prepare the mint — funds were not spent', {
                variant: 'error',
                detail: '',
            })
            return
        }
    } else {
        toast.update(FLOW_TOAST_KIND, 'Minting ordinal', { detail: 'Preparing mint…' })
    }

    // Registered before the mint starts: a small file can finish before the
    // start request returns.
    openSystemThreadWhenMinted(answerId)
    try {
        const mintResult = await api.mintOrdinalTemporal(answerId, {
            treechat_fee_amount_sats: mintPriceSats,
            service_fee_quote: mintFeeQuote.service_fee_quote,
            media_type: mediaKind === 'audio' || mediaKind === 'html' ? mediaKind : 'auto',
            source_file: file,
        })
        if (!mintResult?.workflow_id) {
            throw new Error('Mint workflow did not start')
        }
        failedMintSources.forget(retryKey)
        // Hand the toast slot over without overwriting a faster progress or
        // terminal event that may already have arrived from the workflow.
        showOrdinalMintProgressToast(answerId)
    } catch (error) {
        console.error('Failed to start mint:', error)
        // A source post that is gone or no longer this user's cannot be retried.
        const status = (error as { status?: number } | null)?.status
        if (status === 403 || status === 404) {
            failedMintSources.forget(retryKey)
        } else {
            failedMintSources.remember(retryKey, answerId)
        }
        cancelSystemThreadOpen(answerId)
        toast.update(FLOW_TOAST_KIND, 'Minting failed to start — funds were not spent', {
            variant: 'error',
            // The server says why (fee changed, file too large, wallet missing…).
            detail: error instanceof Error ? error.message : '',
        })
    }
}

// The CTA remains clickable while the picker/tray closes and upload begins. Keep
// one paid workflow active at a time so repeated clicks cannot create duplicate
// public posts and mint attempts.
export const runMintMediaThreadFlow = createMintMediaThreadRunner(performMintMediaThreadFlow)
